import React, { useState, useEffect, useMemo } from "react";
import { toast } from "sonner";
import { Merge, Loader2, AlertTriangle } from "lucide-react";
import api from "../../lib/axios";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import ImageThumbnail from "@/components/common/ImageThumbnail";
import { formatNumber } from "@/utils/formatters";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const MERGE_OPTION_LIMIT = 200;

export const FactoryBulkMergeDialog = ({ open, onOpenChange, selectedTypes, factoryPassword, onMerged }) => {
  const [targetId, setTargetId] = useState(null);
  const [isMerging, setIsMerging] = useState(false);

  useEffect(() => {
    if (open) setTargetId(selectedTypes[0]?.id || null);
  }, [open, selectedTypes]);

  const preview = useMemo(() => {
    const entries = selectedTypes.flatMap((t) => t.entries || []);
    const totalEntries = selectedTypes.reduce((s, t) => s + (t.entries_count || 0), 0);
    const weighted = selectedTypes.reduce(
      (s, t) => s + (t.average_cost_per_m2 || 0) * (t.entries_count || 0),
      0
    );
    return {
      totalEntries,
      avg: totalEntries > 0 ? weighted / totalEntries : 0,
      hasEntries: entries.length > 0,
    };
  }, [selectedTypes]);

  const handleMerge = async () => {
    if (!targetId || selectedTypes.length < 2) return;
    setIsMerging(true);
    try {
      const sourceIds = selectedTypes.map((t) => t.id).filter((id) => id !== targetId);
      const res = await api.post(
        `${API}/factory/merge-types`,
        { source_ids: sourceIds, target_id: targetId },
        { headers: { "X-Factory-Password": factoryPassword } }
      );
      const target = selectedTypes.find((t) => t.id === targetId);
      toast.success(
        `${res.data.merged} tipo${res.data.merged === 1 ? "" : "s"} fusionado${res.data.merged === 1 ? "" : "s"} en "${target?.name}" · ${res.data.new_entries_count} muestras, promedio $${formatNumber(res.data.new_average)}/m²`
      );
      onOpenChange(false);
      onMerged?.({ ...res.data, target_name: target?.name || "" });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al fusionar tipos");
    } finally {
      setIsMerging(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="factory-bulk-merge-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Merge className="w-5 h-5 text-blue-600" />
            Fusionar {selectedTypes.length} tipos de fábrica
          </DialogTitle>
          <DialogDescription>
            Elige el tipo destino. Las muestras de los demás se moverán a él y luego serán eliminados.
          </DialogDescription>
        </DialogHeader>

        <RadioGroup value={targetId || ""} onValueChange={setTargetId} className="max-h-80 overflow-y-auto space-y-2 pr-1">
          {selectedTypes.slice(0, MERGE_OPTION_LIMIT).map((type) => {
            const isTarget = type.id === targetId;
            return (
              <label
                key={type.id}
                htmlFor={`merge-target-${type.id}`}
                className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                  isTarget ? "border-blue-500 bg-blue-50" : "border-slate-200 hover:bg-slate-50"
                }`}
                data-testid={`merge-target-option-${type.id}`}
              >
                <RadioGroupItem value={type.id} id={`merge-target-${type.id}`} />
                <ImageThumbnail src={type.image_data} alt={type.name} className="w-10 h-10 rounded border border-slate-200" />
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-slate-900 truncate">{type.name}</p>
                  <p className="text-xs text-slate-500 truncate">
                    {[type.material, type.series, type.color, type.factory_name].filter(Boolean).join(" · ")}
                  </p>
                </div>
                <div className="text-right text-sm">
                  <p className="font-mono">${formatNumber(type.average_cost_per_m2 || 0)}/m²</p>
                  <p className="text-xs text-slate-500">{type.entries_count || 0} muestras</p>
                </div>
                {isTarget && <Badge className="bg-blue-600">Destino</Badge>}
              </label>
            );
          })}
          {selectedTypes.length > MERGE_OPTION_LIMIT && (
            <p className="text-xs text-slate-500 px-1">
              Solo se listan los primeros {MERGE_OPTION_LIMIT} como posible destino; los {selectedTypes.length} tipos se fusionarán.
            </p>
          )}
        </RadioGroup>

        <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-800">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <div>
            Resultado: <strong>{preview.totalEntries}</strong> muestras, promedio aprox.{" "}
            <strong>${formatNumber(preview.avg)}/m²</strong>. Esta acción no se puede deshacer.
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isMerging} data-testid="merge-cancel-btn">
            Cancelar
          </Button>
          <Button
            onClick={handleMerge}
            disabled={isMerging || !targetId || selectedTypes.length < 2}
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="merge-confirm-btn"
          >
            {isMerging ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Merge className="w-4 h-4 mr-2" />}
            Fusionar {selectedTypes.length - 1} en destino
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default FactoryBulkMergeDialog;
